import React from 'react';
import { Scale, FileCheck, Shield, AlertCircle, CheckCircle, Gavel } from 'lucide-react';
import { useSEO } from '../../hooks/useSEO';
import { Breadcrumb } from '../../components/Breadcrumb';
import { useBanner } from '../../contexts/BannerContext';

export const CognitiveCompliancePage: React.FC = () => {
  useSEO('cognitive-compliance' as any);
  const { isBannerVisible, bannerHeight } = useBanner();

  return (
    <>
      <Breadcrumb />
      <main className="pb-20">
      <section 
        className="px-4 sm:px-6 lg:px-8 mb-20"
        style={{ 
          paddingTop: isBannerVisible ? `${bannerHeight + 64 + 44 + 16}px` : '124px' // 64px header + 44px breadcrumb + 16px extra space
        }}
      >
        <div className="max-w-7xl mx-auto text-center">
          <div className="w-20 h-20 bg-gradient-to-r from-blue-600 to-blue-700 rounded-3xl mx-auto mb-8 flex items-center justify-center">
            <Scale className="w-10 h-10 text-white" />
          </div>
          <h1 
            className="text-5xl md:text-6xl font-bold mb-8 py-4 px-2 leading-loose bg-gradient-to-r from-blue-600 to-blue-700 bg-clip-text text-transparent overflow-visible min-h-fit"
            style={{ lineHeight: '1.8', minHeight: 'auto', overflow: 'visible', paddingTop: '16px', paddingBottom: '16px' }}
          >
            Cognitive Compliance
          </h1>
          <p className="text-xl text-gray-700 dark:text-gray-300 max-w-4xl mx-auto leading-relaxed">
            Navigate complex regulatory landscapes with AI-powered compliance solutions designed to support 
            legal processes, regulatory adherence efforts, and compliance risk management.
          </p>
        </div>
      </section>

      <div className="px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <section className="grid lg:grid-cols-2 gap-12 mb-16">
            <div className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-3xl border border-gray-200 dark:border-white/10 p-8">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">Compliance AI Solutions</h2>
              <div className="space-y-6">
                <div className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mr-3" />
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Regulatory Compliance Automation</h3>
                    <p className="text-gray-700 dark:text-gray-300">Automate compliance monitoring and reporting across multiple regulatory frameworks.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mr-3" />
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Legal Document Analysis</h3>
                    <p className="text-gray-700 dark:text-gray-300">AI-powered contract review, legal document processing, and clause extraction.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mr-3" />
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Audit Trail Automation</h3>
                    <p className="text-gray-700 dark:text-gray-300">Comprehensive audit logging and compliance evidence collection systems.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mr-3" />
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">GDPR & Privacy Compliance</h3>
                    <p className="text-gray-700 dark:text-gray-300">Data protection compliance tools and privacy-by-design frameworks.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-3xl border border-gray-200 dark:border-white/10 p-8">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">Compliance Features</h2>
              <div className="grid grid-cols-2 gap-6">
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl mx-auto mb-4 flex items-center justify-center">
                    <FileCheck className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Document Management</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300">Automated processing & filing</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl mx-auto mb-4 flex items-center justify-center">
                    <Shield className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Risk Monitoring</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300">Real-time compliance tracking</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl mx-auto mb-4 flex items-center justify-center">
                    <AlertCircle className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Alert Systems</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300">Proactive compliance warnings</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl mx-auto mb-4 flex items-center justify-center">
                    <Gavel className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Regulatory Updates</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300">Automatic law change tracking</p>
                </div>
              </div>
            </div>
          </section>

          <section className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-3xl border border-gray-200 dark:border-white/10 p-8 mb-16">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8 text-center">Regulatory Frameworks</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <h3 className="text-xl font-bold text-blue-600 dark:text-blue-400 mb-4">Data Protection</h3>
                <p className="text-gray-700 dark:text-gray-300">
                  GDPR, CCPA, HIPAA compliance automation with data mapping, consent management, and breach response protocols.
                </p>
              </div>
              <div className="text-center">
                <h3 className="text-xl font-bold text-blue-600 dark:text-blue-400 mb-4">Financial Services</h3>
                <p className="text-gray-700 dark:text-gray-300">
                  SOX, PCI-DSS, AML/KYC compliance with automated reporting, transaction monitoring, and risk assessment.
                </p>
              </div>
              <div className="text-center">
                <h3 className="text-xl font-bold text-blue-600 dark:text-blue-400 mb-4">Industry Standards</h3>
                <p className="text-gray-700 dark:text-gray-300">
                  ISO 27001, NIST, FDA compliance with policy management, control testing, and certification support.
                </p>
              </div>
            </div>
          </section>

          <section className="bg-gradient-to-r from-blue-600/10 to-blue-700/10 rounded-3xl border border-blue-200 dark:border-blue-500/30 p-8 mb-16">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">Compliance Benefits</h2>
              <p className="text-gray-700 dark:text-gray-300 text-lg">
                AI-powered compliance solutions designed to support business operations
              </p>
            </div>
            <div className="grid md:grid-cols-4 gap-6 text-center">
              <div>
                <div className="text-4xl font-bold text-blue-600 dark:text-blue-400 mb-2">Reduced</div>
                <div className="text-sm text-gray-600 dark:text-gray-300">Compliance Costs</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-blue-600 dark:text-blue-400 mb-2">Faster</div>
                <div className="text-sm text-gray-600 dark:text-gray-300">Audit Preparation</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-blue-600 dark:text-blue-400 mb-2">Enhanced</div>
                <div className="text-sm text-gray-600 dark:text-gray-300">Accuracy</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-blue-600 dark:text-blue-400 mb-2">24/7</div>
                <div className="text-sm text-gray-600 dark:text-gray-300">Continuous Monitoring</div>
              </div>
            </div>
          </section>

          <section className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-3xl border border-gray-200 dark:border-white/10 p-8 text-center">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">
              Stay Compliant with Confidence
            </h2>
            <p className="text-gray-700 dark:text-gray-300 text-lg mb-8 max-w-3xl mx-auto">
              Navigate regulatory complexity with intelligent compliance solutions designed to support 
              risk management, adherence efforts, and business growth objectives.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => window.location.href = '/contact'}
                className="px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
              >
                Ensure Compliance
              </button>
              <button
                onClick={() =>
                  window.open(
                    "https://outlook.office.com/book/CognitiveCraftHub2@cognitivecrafthub.com/?ismsaljsauthenabled",
                    "_blank"
                  )
                }
                className="px-8 py-4 border-2 border-gray-300 dark:border-white/30 text-gray-700 dark:text-white font-semibold rounded-lg hover:bg-gray-100 dark:hover:bg-white/10 hover:border-gray-400 dark:hover:border-white/50 transition-all duration-300"
              >
                Compliance Assessment
              </button>
            </div>
          </section>

          {/* Legal Disclaimer */}
          <section className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-6 border border-gray-200 dark:border-gray-600 mt-20">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Important Compliance Notice</h3>
            <div className="space-y-3 text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
              <p>
                <strong>Technology Solutions Only:</strong> Cognitive Compliance provides technology solutions designed to support 
                compliance efforts. Our services do not constitute legal advice, regulatory guidance, or guarantee compliance 
                with any specific laws or regulations.
              </p>
              <p>
                <strong>Professional Consultation Required:</strong> All compliance decisions should be made in consultation 
                with qualified legal and regulatory professionals familiar with your specific jurisdiction and industry requirements.
              </p>
              <p>
                <strong>No Guarantee of Outcomes:</strong> While our solutions are designed to support compliance processes, 
                we cannot guarantee specific outcomes, regulatory approval, or protection from legal or regulatory action.
              </p>
              <p>
                <strong>Regulatory Variability:</strong> Laws and regulations vary by jurisdiction and change frequently. 
                Users are responsible for ensuring compliance with applicable current laws and consulting appropriate experts.
              </p>
            </div>
          </section>
        </div>
      </div>
    </main>
    </>
  );
};